import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { PMField } from "../../../utils/PMField";
import { axiosWithToast } from "../../../Utils";
import {
  createEmployee,
  downloadEmployeeContractDraft,
  updateEmployee,
} from "../../../../api/user/employment";
import { fetchCompanyEmployeePositions } from "../../../../api/companyEmployeePositions";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { PMNotice } from "../../../utils/notifications";
import EmploymentSalariesTable from "./employee/employmentSalariesTable";
import HourlyCostAndRatesTable from "./employee/hourlyCostAndRatesTable";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { PMButton } from "../../../utils/PMButton";
import PMListboxFormik from "../../../utils/PMListboxFormik";
import { PMDialog } from "../../../utils/PMDialog";
import {
  DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU,
  PromangRichTextEditor,
} from "../../../utils/PromangRichTextEditor";
import { SigningPanel } from "../../signing/SigningPanel";

const agreementTypeOptions = [
  { value: "", label: "user_employment.option_not_selected" },
  { value: "fast", label: "user_employment.agreement_type_permanent" },
  { value: "temporary", label: "user_employment.agreement_type_temporary" },
  { value: "hourly", label: "user_employment.agreement_type_hourly" },
  { value: "seasonal", label: "user_employment.agreement_type_seasonal" },
  { value: "other", label: "user_employment.agreement_type_other" },
];

const wageUnitOptions = [
  { value: "", label: "user_employment.option_not_selected" },
  { value: "hour", label: "user_employment.wage_unit_hour" },
  { value: "month", label: "user_employment.wage_unit_month" },
  { value: "year", label: "user_employment.wage_unit_year" },
  { value: "fixed", label: "user_employment.wage_unit_fixed" },
];

const FormSection = ({ title, description, children }) => (
  <div className="mt-5 overflow-visible shadow sm:rounded-md">
    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
      <div>
        <h3 className="text-lg font-medium leading-6 text-gray-900">{title}</h3>
        {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      </div>
      <div className="grid grid-cols-6 gap-6">{children}</div>
    </div>
  </div>
);

const RichTextFormikField = ({ label, name, values, setFieldValue, placeholder }) => {
  const { t } = useTranslation();

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t(label)}
      </label>
      <PromangRichTextEditor
        value={values?.[name] || ""}
        onChange={(html) => setFieldValue(name, html)}
        placeholder={t(placeholder || label)}
        size="md"
        contextMenu={DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU}
      />
    </div>
  );
};

const formatTimeRange = (from, to) => {
  if (from && to) return `${from} - ${to}`;
  return from || to || "";
};

const contractRow = (label, value, rich = false) => {
  if (!value) return null;

  return (
    <div className="grid grid-cols-1 gap-1 border-b border-gray-100 py-3 sm:grid-cols-3" key={label}>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900 sm:col-span-2">
        {rich ? (
          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: value }} />
        ) : (
          value
        )}
      </dd>
    </div>
  );
};

const resolvePositionTitle = (values, positionOptions, employment) => {
  const id = values?.employee_position_id;
  if (id != null && id !== "") {
    const match = positionOptions.find(
      (option) => option.value != null && Number(option.value) === Number(id)
    );
    if (match?.label) return match.label;
  }

  return (
    employment?.employee_position?.displayTitle ||
    employment?.employee_position?.name ||
    employment?.working_posistion ||
    ""
  );
};

const EmploymentContractDraftPreview = ({ values, userItem, employment, positionOptions }) => {
  const { t } = useTranslation();
  const employeeName = userItem?.displayTitle || employment?.name || "";
  const regularTime = formatTimeRange(values?.worktime_regular_from, values?.worktime_regular_too);
  const weekendTime = formatTimeRange(values?.worktime_weekend_from, values?.worktime_weekend_too);
  const wageText = [values?.wages, values?.wages_unit].filter(Boolean).join(" ");

  return (
    <div className="space-y-6">
      <PMNotice
        severity="info"
        title={t("user_employment.draft_notice_title")}
        description={t("user_employment.draft_notice")}
      />

      <article className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="border-b border-gray-200 pb-4">
          <p className="text-sm font-medium uppercase tracking-wide text-gray-500">
            {t("user_employment.contract_draft_label")}
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-gray-900">
            {t("user_employment.contract_title")}
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            {t("user_employment.contract_draft_for", { name: employeeName || "-" })}
          </p>
        </div>

        <dl className="mt-4">
          {[
            contractRow(t("user_employment.employee_name"), employeeName),
            contractRow(t("Email"), employment?.email || userItem?.email),
            contractRow(t("Mobile"), employment?.mobile || employment?.phone || userItem?.mobile),
            contractRow(t("user_employment.social_security"), values?.social_security),
            contractRow(t("user_employment.employee_number"), values?.employee_number),
            contractRow(
              t("user_employment.working_posistion"),
              resolvePositionTitle(values, positionOptions, employment)
            ),
            contractRow(t("user_employment.workplace"), values?.workplace),
            contractRow(t("user_employment.agreement_type"), values?.agreement_type),
            contractRow(t("user_employment.employment_starts"), values?.employment_starts),
            contractRow(t("user_employment.employment_ends"), values?.employment_ends),
            contractRow(t("user_employment.worktime_regular_range"), regularTime),
            contractRow(t("user_employment.worktime_weekend_range"), weekendTime),
            contractRow(t("user_employment.wages"), wageText),
            contractRow(t("user_employment.overtime_pay"), values?.overtime_pay),
            contractRow(t("user_employment.dismissal"), values?.dismissal),
            contractRow(t("user_employment.wage_other_addition"), values?.wage_other_addition),
            contractRow(t("user_employment.wage_weekend_and_night_addition"), values?.wage_weekend_and_night_addition),
            contractRow(t("user_employment.collective_agreement"), values?.collective_agreement),
            contractRow(t("user_employment.employment_instructions"), values?.employment_instructions, true),
            contractRow(t("user_employment.employment_allowance"), values?.employment_allowance, true),
            contractRow(t("user_employment.notes"), values?.notes, true),
            contractRow(t("user_employment.other"), values?.other, true),
            contractRow(t("user_employment.signed_place"), values?.signed_place),
            contractRow(t("user_employment.signed_at"), values?.signed_at),
            contractRow(t("user_employment.account_number"), values?.account_number),
          ].filter(Boolean)}
        </dl>
      </article>
    </div>
  );
};

const downloadBlob = (response, fallbackName) => {
  const contentType = response.headers?.["content-type"] || "";
  if (!contentType.includes("application/pdf")) {
    throw new Error("Expected PDF response");
  }

  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const disposition = response.headers?.["content-disposition"] || "";
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/);

  link.href = url;
  link.download = filenameMatch?.[1] || fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const UserEmployment = (props) => {
  const { t } = useTranslation();
  const { userItem, onRefresh: onParentRefresh } = props;

  const [employment, setEmployment] = useState(userItem?.employment);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newEmploymentDialogOpen, setNewEmploymentDialogOpen] = useState(false);
  const [draftPreviewOpen, setDraftPreviewOpen] = useState(false);
  const [draftPdfLoading, setDraftPdfLoading] = useState(false);
  const [employeePositionOptions, setEmployeePositionOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchCompanyEmployeePositions()
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.data) ? data.data : [];
        const options = [
          { value: null, label: "user_employment.employee_position_none" },
          ...rows
            .filter((row) => row.active !== false)
            .map((row) => ({
              value: row.id != null ? Number(row.id) : null,
              label: row.displayTitle || row.name || `#${row.id}`,
            })),
        ];
        setEmployeePositionOptions(options);
      })
      .catch(() => {
        if (!cancelled) {
          setEmployeePositionOptions([{ value: null, label: "user_employment.employee_position_none" }]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (userItem?.employment) {
      setEmployment(userItem.employment);
      setIsLoading(false);
    }
  }, [userItem]);



  const handleCreateSubmit = (values, helpers) => {
    axiosWithToast(
      createEmployee(values),
      (data) => {
        setEmployment(data);
        setIsLoading(false);
        helpers.setStatus({ success: true });
        helpers.setSubmitting(false);
      },
      () => {
        helpers.setSubmitting(false);
      },
      () => {
        setNewEmploymentDialogOpen(false);
      },
      "Lagrer ansettelse",
      (error) => {
        setErrors(error);
        helpers.setErrors({ submit: "An error occurred while saving." });
        helpers.setSubmitting(false);
      }
    ).catch((err) => {
      console.error("Submission error:", err);
      helpers.setErrors({ submit: err.message });
      helpers.setSubmitting(false);
    });
  };

  const handleDownloadDraftPdf = async () => {
    if (!userItem?.id || !employment?.id) return;

    setDraftPdfLoading(true);
    try {
      const response = await downloadEmployeeContractDraft({
        user_id: userItem.id,
        employment_id: employment.id,
      });
      downloadBlob(response, `arbeidsavtale-utkast-${employment.id}.pdf`);
    } catch (error) {
      toast.error(t("user_employment.draft_pdf_download_failed"));
    } finally {
      setDraftPdfLoading(false);
    }
  };

  const renderCreateDialog = (
    <PMDialog
      title="Oppretter ansettelse"
      open={newEmploymentDialogOpen}
      onClose={() => setNewEmploymentDialogOpen(false)}
    >
      <Formik
        initialValues={{
          user_id: userItem?.id,
          employment_starts: "",
          employment_ends: "",
          social_security: userItem?.social_security || userItem?.employment?.social_security || "",
          employee_position_id:
            userItem?.employment?.employee_position_id != null
              ? Number(userItem.employment.employee_position_id)
              : null,
          workplace: userItem?.employment?.workplace || "",
        }}
        validationSchema={Yup.object({
          description: Yup.string(),
        })}
        onSubmit={handleCreateSubmit}
      >
        {(formik) => (
          <Form>
            {errors &&
              errors.length > 0 &&
              errors.map((error, index) => (
                <PMNotice
                  severity={"error"}
                  key={index}
                  title="Error!"
                  description={error}
                />
              ))}

            <div className="grid grid-cols-2 sm:grid-cols-2 gap-6">
              <div className="col-span-3 sm:col-span-3">
                Du oppretter nå ansettelse for {userItem?.displayTitle}
              </div>

              <div className="col-span-2 sm:col-span-2">
                <PMField
                  label="user_employment.employment_starts"
                  name="employment_starts"
                  type="date"
                  {...formik}
                />
              </div>

              <div className="col-span-2 sm:col-span-2">
                <PMField
                  label="user_employment.employment_ends"
                  name="employment_ends"
                  type="date"
                  {...formik}
                />
              </div>

              <div className="col-span-2 sm:col-span-2">
                <PMField
                  label="user_employment.social_security"
                  name="social_security"
                  type="text"
                  {...formik}
                />
              </div>

              <div className="col-span-2 sm:col-span-2">
                <PMListboxFormik
                  label={t("user_employment.working_posistion")}
                  name="employee_position_id"
                  options={employeePositionOptions}
                  {...formik}
                />
              </div>

              <div className="col-span-2 sm:col-span-2">
                <PMField
                  label="user_employment.workplace"
                  name="workplace"
                  type="text"
                  {...formik}
                />
              </div>

              <div className="col-span-2 sm:col-span-2 flex justify-between">
                <PMButton
                  type="button"
                  onClick={() => setNewEmploymentDialogOpen(false)}
                  className="bg-slate-400 hover:bg-slate-200 max-w-1/2"
                >
                  Avbryt
                </PMButton>
                <PMButton className="ml-10 max-w-1/2" type="submit">
                  Lagre
                </PMButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );

  const renderForm = () => {
    const initialValues = {
      user_id: userItem?.id,
      employment_id: employment?.id,
      employee_number: employment?.employee_number,
      social_security: employment?.social_security,
      employee_salaries: employment?.employee_salaries || [],
      user_hourly_cost_and_rates: employment?.user_hourly_cost_and_rates || [],
      employment_starts: employment?.employment_starts,
      employment_ends: employment?.employment_ends || employment?.end_date || "",
      employee_position_id:
        employment?.employee_position_id != null ? Number(employment.employee_position_id) : null,
      workplace: employment?.workplace || "",
      agreement_type: employment?.agreement_type || "",
      wages: employment?.wages || "",
      wages_unit: employment?.wages_unit || "",
      dismissal: employment?.dismissal || "",
      worktime_regular_from: employment?.worktime_regular_from || "",
      worktime_regular_too: employment?.worktime_regular_too || "",
      worktime_weekend_from: employment?.worktime_weekend_from || "",
      worktime_weekend_too: employment?.worktime_weekend_too || "",
      employment_instructions: employment?.employment_instructions || "",
      employment_allowance: employment?.employment_allowance || "",
      overtime_pay: employment?.overtime_pay || "",
      wage_other_addition: employment?.wage_other_addition || "",
      wage_weekend_and_night_addition: employment?.wage_weekend_and_night_addition || "",
      collective_agreement: employment?.collective_agreement || "",
      account_number: employment?.account_number || "",
      signed_place: employment?.signed_place || "",
      signed_at: employment?.signed_at || "",
      notes: employment?.notes || "",
      other: employment?.other || "",
      submit: null,
    };

    return (
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={Yup.object({})}
        onSubmit={(values, helpers) => {
          axiosWithToast(
            updateEmployee(values),
            (data) => {
              setEmployment(data);
              if (typeof onParentRefresh === "function") {
                onParentRefresh({ ...userItem, employment: data });
              }
              helpers.setStatus({ success: true });
              helpers.setSubmitting(false);
            },
            () => {
              setIsLoading(false);
              helpers.setSubmitting(false);
            },
            () => {},
            "Bruker ble oppdatert",
            (error) => {
              setErrors(error);
              setIsLoading(false);
              helpers.setErrors({
                submit: "An error occurred while saving.",
              });
              helpers.setSubmitting(false);
            }
          ).catch((err) => {
            console.error("Submission error:", err);
            helpers.setErrors({ submit: err.message });
            helpers.setSubmitting(false);
          });
        }}
      >
        {(formik) => (
          <Form>
            <FormSection
              title={t("user_employment.section_basic")}
              description={t("user_employment.section_basic_hint")}
            >
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="employee_number"
                  label="user_employment.employee_number"
                  type="text"
                  disabled
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="employment_starts"
                  label="user_employment.employment_starts"
                  type="date"
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="employment_ends"
                  label="user_employment.employment_ends"
                  type="date"
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="social_security"
                  label="user_employment.social_security"
                  type="text"
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMListboxFormik
                  label={t("user_employment.working_posistion")}
                  name="employee_position_id"
                  options={employeePositionOptions}
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="workplace"
                  label="user_employment.workplace"
                  type="text"
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMListboxFormik
                  label={t("user_employment.agreement_type")}
                  name="agreement_type"
                  options={agreementTypeOptions}
                  {...formik}
                />
              </div>
            </FormSection>

            <FormSection
              title={t("user_employment.section_worktime")}
              description={t("user_employment.section_worktime_hint")}
            >
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="worktime_regular_from"
                  label="user_employment.worktime_regular_from"
                  type="time"
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="worktime_regular_too"
                  label="user_employment.worktime_regular_too"
                  type="time"
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="worktime_weekend_from"
                  label="user_employment.worktime_weekend_from"
                  type="time"
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="worktime_weekend_too"
                  label="user_employment.worktime_weekend_too"
                  type="time"
                  {...formik}
                />
              </div>
            </FormSection>

            <FormSection
              title={t("user_employment.section_wages")}
              description={t("user_employment.section_wages_hint")}
            >
              <div className="col-span-6 sm:col-span-3">
                <PMField name="wages" label="user_employment.wages" type="text" {...formik} />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMListboxFormik
                  label={t("user_employment.wages_unit")}
                  name="wages_unit"
                  options={wageUnitOptions}
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField name="overtime_pay" label="user_employment.overtime_pay" type="text" {...formik} />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField name="dismissal" label="user_employment.dismissal" type="text" {...formik} />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField name="wage_other_addition" label="user_employment.wage_other_addition" type="text" {...formik} />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField name="wage_weekend_and_night_addition" label="user_employment.wage_weekend_and_night_addition" type="text" {...formik} />
              </div>
              <div className="col-span-6">
                <PMField name="collective_agreement" label="user_employment.collective_agreement" type="text" {...formik} />
              </div>
            </FormSection>

            <FormSection
              title={t("user_employment.section_contract_text")}
              description={t("user_employment.section_contract_text_hint")}
            >
              <div className="col-span-6">
                <RichTextFormikField
                  name="employment_instructions"
                  label="user_employment.employment_instructions"
                  {...formik}
                />
              </div>
              <div className="col-span-6">
                <RichTextFormikField
                  name="employment_allowance"
                  label="user_employment.employment_allowance"
                  {...formik}
                />
              </div>
              <div className="col-span-6">
                <RichTextFormikField name="notes" label="user_employment.notes" {...formik} />
              </div>
              <div className="col-span-6">
                <RichTextFormikField name="other" label="user_employment.other" {...formik} />
              </div>
            </FormSection>

            <FormSection
              title={t("user_employment.section_signature")}
              description={t("user_employment.section_signature_hint")}
            >
              <div className="col-span-6 sm:col-span-2">
                <PMField name="signed_place" label="user_employment.signed_place" type="text" {...formik} />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <PMField name="signed_at" label="user_employment.signed_at" type="date" {...formik} />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <PMField name="account_number" label="user_employment.account_number" type="text" {...formik} />
              </div>
            </FormSection>

            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6">
                    <EmploymentSalariesTable
                      number
                      inactive
                      drag={false}
                      {...formik}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {t("user_employment.hourly_costs_title")}
                    </h3>
                    <HourlyCostAndRatesTable drag={true} {...formik} />
                  </div>
                </div>
              </div>
            </div>

            <FormSection
              title={t("user_employment.section_contract_actions")}
              description={t("user_employment.section_contract_actions_hint")}
            >
              <div className="col-span-6">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <PMButton type="button" styleType="minimal" onClick={() => setDraftPreviewOpen(true)}>
                    {t("user_employment.preview_contract_draft")}
                  </PMButton>
                  <PMButton
                    type="button"
                    styleType="minimal"
                    loading={draftPdfLoading}
                    onClick={handleDownloadDraftPdf}
                  >
                    {t("user_employment.download_contract_draft_pdf")}
                  </PMButton>
                </div>
              </div>
              <div className="col-span-6">
                <SigningPanel
                  signableType="Employee"
                  signableId={employment?.id}
                  userId={userItem?.id}
                  employeeId={employment?.id}
                  title={t("user_employment.contract_title")}
                  nonSensitiveTitle={t("user_employment.contract_title")}
                  description={t("user_employment.contract_description")}
                  defaultSigner={{
                    name: userItem?.displayTitle || employment?.name || "",
                    email: employment?.email || userItem?.email || "",
                    mobile: employment?.mobile || employment?.phone || userItem?.mobile_work || userItem?.mobile || "",
                    national_identity_number: employment?.social_security || userItem?.social_security || "",
                  }}
                />
              </div>
            </FormSection>
            <PMDialog
              title={t("user_employment.preview_contract_draft")}
              open={draftPreviewOpen}
              onClose={() => setDraftPreviewOpen(false)}
              size="5xl"
            >
              <div className="mb-4 flex justify-end">
                <div className="w-full sm:w-64">
                  <PMButton
                    type="button"
                    loading={draftPdfLoading}
                    onClick={handleDownloadDraftPdf}
                  >
                    {t("user_employment.download_contract_draft_pdf")}
                  </PMButton>
                </div>
              </div>
              <EmploymentContractDraftPreview
                values={formik.values}
                userItem={userItem}
                employment={employment}
                positionOptions={employeePositionOptions}
              />
            </PMDialog>
            <FormikSaveBar {...formik} />
          </Form>
        )}
      </Formik>
    );
  };

  // Innleid (hired, non-employee) users cannot be employed.
  const isHired = userItem?.user_type_id === 3;

  if (isHired) {
    return (
      <PMNotice
        severity="info"
        title={t("user_employment.hired_no_employment_title")}
        description={t("user_employment.hired_no_employment")}
      />
    );
  }

  return (
    <>
      {!employment?.id && (
        <>
          <PMNotice
            severity="warning"
            title="Mangler ansettelse"
            description="Denne brukeren er ikke ansatt eller arbeidsforhold er ikke opprettet."
          />
          <PMButton onClick={() => setNewEmploymentDialogOpen(true)}>
            Opprett ansettelse
          </PMButton>
        </>
      )}

      {renderCreateDialog}

      {employment?.id && isLoading && <ResourceLoading />}
      {employment?.id && !isLoading && renderForm()}
    </>
  );
};
